import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hold-to-record hook using MediaRecorder.
 *
 * Usage:
 *   const rec = useVoiceRecorder(onStop);
 *   <button onMouseDown={rec.start} onMouseUp={rec.stop} onMouseLeave={rec.stop}
 *           onTouchStart={rec.start} onTouchEnd={rec.stop}>...</button>
 *
 * onStop is called with a Blob when a recording completes normally.
 */
export function useVoiceRecorder(onStop) {
  const [state, setState] = useState("idle"); // idle | requesting | recording | error
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAt = useRef(0);
  const stoppedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRef.current = null;
    chunksRef.current = [];
  }, []);

  const start = useCallback(async () => {
    if (state === "recording" || state === "requesting") return;
    setError(null);
    setState("requesting");
    stoppedRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        cleanup();
        setState("idle");
        setElapsed(0);
        if (!stoppedRef.current && blob.size > 300) {
          stoppedRef.current = true;
          onStop?.(blob);
        }
      };
      rec.onerror = (e) => {
        setError(e?.error?.message || "recorder error");
        setState("error");
        cleanup();
      };
      rec.start();
      startedAt.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
      }, 250);
      setState("recording");
    } catch (e) {
      setError(e?.message || "Microphone unavailable");
      setState("error");
      cleanup();
    }
  }, [state, onStop, cleanup]);

  const stop = useCallback(() => {
    if (state !== "recording") return;
    const rec = mediaRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* ignore */ }
    }
  }, [state]);

  const cancel = useCallback(() => {
    stoppedRef.current = true;
    const rec = mediaRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* ignore */ }
    }
    cleanup();
    setState("idle");
    setElapsed(0);
  }, [cleanup]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { state, error, elapsed, start, stop, cancel };
}
